import { useEffect, useRef, useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface LatexTextProps {
  text: string;
  className?: string;
}

type Segment = 
  | { type: 'text'; content: string }
  | { type: 'math'; content: string; displayMode: boolean };

const DELIMITERS = [
  { open: '$$', close: '$$', display: true },
  { open: '\\[', close: '\\]', display: true },
  { open: '\\(', close: '\\)', display: false },
  { open: '$', close: '$', display: false },
];

/**
 * Unescape the LaTeX escapes we support outside maths mode.
 *
 * `\$` is the correct LaTeX for a literal dollar sign, and the question bank stores Jamaican
 * currency that way in 612 places ("\$500 - \$213.14 ="). Nothing ever removed the backslash,
 * so parents read "\$500". The repair belongs here and not in the data: rewriting those rows to
 * a bare `$` would make the two dollars in "$500 - $213.14" look like maths delimiters and
 * KaTeX would typeset "500 - " as an equation.
 */
function unescapeText(s: string): string {
  return s.replace(/\\([$%&#_{}])/g, "$1");
}

function parseLatex(input: string): Segment[] {
  const segments: Segment[] = [];
  let pos = 0;

  const pushText = (content: string) => {
    const last = segments[segments.length - 1];
    if (last && last.type === 'text') last.content += content;
    else segments.push({ type: 'text', content });
  };

  while (pos < input.length) {
    let foundDelim = false;

    // An escaped delimiter is literal text. Consume both characters and emit the bare symbol,
    // otherwise the backslash survives into what the reader sees.
    if (input[pos] === '\\' && '$%&#_{}'.includes(input[pos + 1])) {
      pushText(input[pos + 1]);
      pos += 2;
      continue;
    }

    for (const delim of DELIMITERS) {
      if (input.substring(pos, pos + delim.open.length) !== delim.open) continue;

      const contentStart = pos + delim.open.length;
      const closeIdx = input.indexOf(delim.close, contentStart + (delim.open === delim.close ? 1 : 0));

      if (closeIdx === -1) continue;

      const mathContent = input.substring(contentStart, closeIdx);

      if (mathContent.trim().length === 0) {
        pos = closeIdx + delim.close.length;
        foundDelim = true;
        break;
      }

      segments.push({ type: 'math', content: mathContent, displayMode: delim.display });
      pos = closeIdx + delim.close.length;
      foundDelim = true;
      break;
    }

    if (!foundDelim) {
      pushText(input[pos]);
      pos++;
    }
  }

  return segments;
}

function hasLatex(text: string): boolean {
  return /\$[^$]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]/.test(text);
}

function MathSegment({ content, displayMode }: { content: string; displayMode: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (ref.current) {
      try {
        katex.render(content, ref.current, {
          displayMode,
          throwOnError: false,
          strict: false,
        });
      } catch {
        if (ref.current) {
          ref.current.textContent = content;
        }
      }
    }
  }, [content, displayMode]);

  return displayMode ? (
    <span ref={ref} className="block my-2 text-center" />
  ) : (
    <span ref={ref} />
  );
}

export function LatexText({ text, className = '' }: LatexTextProps) {
  const segments = useMemo(() => {
    if (!text || !hasLatex(text)) return null;
    return parseLatex(text);
  }, [text]);

  if (!segments) {
    // No maths here, but the text can still carry escapes. A lone "\$200" never reaches
    // parseLatex, because hasLatex only fires when it sees a matched pair of dollars.
    return <span className={className}>{unescapeText(text)}</span>;
  }

  return (
    <span className={className}>
      {segments.map((seg, i) =>
        seg.type === 'text' ? (
          <span key={i}>{seg.content}</span>
        ) : (
          <MathSegment key={i} content={seg.content} displayMode={seg.displayMode} />
        )
      )}
    </span>
  );
}
